// Supabase browser client.
// Session storage: first-party cookies (via @supabase/ssr), NOT localStorage —
// see src/integrations/supabase/cookie-config.ts for why.
import { createClient } from '@supabase/supabase-js';
import { createBrowserClient } from '@supabase/ssr';
import type { Database } from './types';
import { createSupabaseFetch, supabaseCookieOptions } from './cookie-config';

export function getSupabaseBrowserEnv() {
  // Use import.meta.env for client-side (Vite build-time replacement)
  // Fall back to process.env for SSR (server-side rendering)
  const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || process.env.SUPABASE_URL;
  const SUPABASE_PUBLISHABLE_KEY =
    import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY || process.env.SUPABASE_PUBLISHABLE_KEY;

  if (!SUPABASE_URL || !SUPABASE_PUBLISHABLE_KEY) {
    const missing = [
      ...(!SUPABASE_URL ? ['SUPABASE_URL'] : []),
      ...(!SUPABASE_PUBLISHABLE_KEY ? ['SUPABASE_PUBLISHABLE_KEY'] : []),
    ];
    const message = `Missing Supabase environment variable(s): ${missing.join(', ')}. Connect Supabase in Lovable Cloud.`;
    console.error(`[Supabase] ${message}`);
    throw new Error(message);
  }

  return { SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY };
}

/**
 * Cookies are not always writable: inside a cross-site iframe (the Lovable
 * editor preview) Safari/Chrome block third-party cookie writes, which would
 * silently drop the session right after a successful sign-in ("Вы вошли в
 * аккаунт", but the app stays on /auth). Detect that and fall back to
 * localStorage in those environments.
 */
function cookiesWritable(): boolean {
  try {
    const key = '__sb_cookie_test';
    document.cookie = `${key}=1; path=/; SameSite=Lax`;
    const ok = document.cookie.includes(`${key}=1`);
    document.cookie = `${key}=; path=/; Max-Age=0`;
    return ok;
  } catch {
    return false;
  }
}

function createSupabaseClient() {
  const { SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY } = getSupabaseBrowserEnv();

  // SSR / prerender: no document, no session persistence.
  if (typeof document === 'undefined') {
    return createClient<Database>(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
      global: { fetch: createSupabaseFetch(SUPABASE_PUBLISHABLE_KEY) },
      auth: { storage: undefined, persistSession: false, autoRefreshToken: false },
    });
  }

  if (!cookiesWritable()) {
    return createClient<Database>(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
      global: { fetch: createSupabaseFetch(SUPABASE_PUBLISHABLE_KEY) },
      auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true },
    });
  }

  const secure = window.location.protocol === 'https:';

  return createBrowserClient<Database>(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
    global: { fetch: createSupabaseFetch(SUPABASE_PUBLISHABLE_KEY) },
    cookieOptions: supabaseCookieOptions(secure),
  });
}


let _supabase: ReturnType<typeof createSupabaseClient> | undefined;

// Import the supabase client like this:
// import { supabase } from "@/integrations/supabase/client";
export const supabase = new Proxy({} as ReturnType<typeof createSupabaseClient>, {
  get(_, prop, receiver) {
    if (!_supabase) _supabase = createSupabaseClient();
    return Reflect.get(_supabase, prop, receiver);
  },
});

/** True when the browser session lives in cookies (false → localStorage fallback). */
export function supabaseUsesCookieStorage(): boolean {
  return typeof document !== 'undefined' && cookiesWritable();
}
